import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import clsx from "clsx";
import { type ComponentProps, type JSX, useEffect } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";

import RenderIf from "common/components/RenderIf";
import Toolbar from "common/components/Templates/TemplateEditor/Toolbar";
import TEMPLATE_VARIABLES from "common/constants/templateVariables";
import {
  extractAiDescriptors,
  parseTemplateText,
  stringifyTemplateContent,
} from "common/helpers/utils";
import type { Template } from "common/types";

import AiVariableExtension from "./AiVariableExtension";
import VariableExtension from "./VariableExtension";
import VariableList from "./VariableList";

function getVariableExtension(
  customVariables: { name: string; value: string }[],
) {
  return VariableExtension.configure({
    deleteTriggerWithBackspace: true,
    suggestion: {
      char: `{{`,
      items: ({ query }: { query: string }) =>
        [...customVariables, ...TEMPLATE_VARIABLES].filter(({ name }) =>
          name.toLowerCase().includes(query.toLowerCase()),
        ),
      render: () => {
        let reactRenderer: ReactRenderer<
          VariableListRef,
          ComponentProps<typeof VariableList>
        > = null;
        let popup: TippyInstance[] | null = null;

        return {
          onStart: (onStartProps) => {
            if (!onStartProps.clientRect) {
              return;
            }

            reactRenderer = new ReactRenderer(VariableList, {
              props: {
                ...onStartProps,
                customVariables,
              },
              editor: onStartProps.editor,
            });

            popup = tippy("#editorContent", {
              getReferenceClientRect: onStartProps.clientRect,
              content: reactRenderer.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start",
            });
          },

          onUpdate(onUpdateProps) {
            reactRenderer?.updateProps(onUpdateProps);

            if (!onUpdateProps.clientRect) {
              return;
            }

            popup?.[0].setProps({
              getReferenceClientRect: onUpdateProps.clientRect,
            });
          },

          onKeyDown(onKeyDownProps) {
            if (onKeyDownProps.event.key === "Escape") {
              popup?.[0].hide();

              return true;
            }

            return reactRenderer?.ref?.onKeyDown(onKeyDownProps);
          },

          onExit() {
            popup?.[0].destroy();
            reactRenderer?.destroy();
          },
        };
      },
    },
  });
}

interface VariableListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean | undefined;
}
interface TemplateEditorProps {
  initialData: Pick<Template, "message" | "ai_descriptors"> | undefined;
  onChange: (newValues: Pick<Template, "message" | "ai_descriptors">) => void;
  isEditable: boolean;
  messageMaxLength: number;
  customVariables?: { name: string; value: string }[];
  languageDropdownComponent?: JSX.Element;
  autoFocus?: boolean;
}

export default function TemplateEditor({
  initialData,
  onChange,
  isEditable,
  messageMaxLength,
  customVariables = [],
  languageDropdownComponent = null,
  autoFocus = false,
}: TemplateEditorProps) {
  const editor = useEditor({
    editable: isEditable,
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      AiVariableExtension,
      getVariableExtension(customVariables),
    ],
    content: parseTemplateText(
      initialData ? initialData.message : "",
      customVariables,
      initialData ? initialData.ai_descriptors : {},
    ),
    onUpdate: ({ editor: templateEditor }) => {
      const editorJSON = templateEditor.getJSON();
      const newData = {
        ai_descriptors: extractAiDescriptors(editorJSON.content),
        message: stringifyTemplateContent(editorJSON),
      };
      onChange(newData);
    },
    editorProps: {
      attributes: {
        class:
          "text-md lg:text-sm h-full focus:outline-none [&_.node-variable]:align-middle [&_.node-aiVariable]:align-middle",
      },
    },
    shouldRerenderOnTransaction: false,
  });

  useEffect(() => {
    editor.setEditable(isEditable, true);
    if (autoFocus) {
      if (isEditable) {
        editor.commands.focus("end");
      }
    }
  }, [isEditable]);

  const characterCount = stringifyTemplateContent(editor.getJSON()).length;

  return (
    <>
      <EditorContent
        id="editorContent"
        editor={editor}
        className={clsx(
          "mb-4 flex-1 overflow-y-auto p-2 lg:p-3 transition-[color] scrollbar-thin",
          !isEditable && "text-black-500",
        )}
      />
      <div className="flex flex-wrap items-end justify-between gap-2 mb-2 mx-2">
        {languageDropdownComponent}
        <span
          className={clsx(
            "ml-auto text-nowrap text-xs",
            characterCount >= messageMaxLength
              ? "text-red-500"
              : "text-black-500",
          )}
        >
          {characterCount}/{messageMaxLength}
        </span>
      </div>
      <RenderIf condition={isEditable}>
        <span className="mx-1 lg:mx-2 h-px bg-black-200" />
        <Toolbar editor={editor} customVariables={customVariables} />
      </RenderIf>
    </>
  );
}
